import { createTheme } from '@mui/material/styles'
import type { TypographyStyleOptions } from '@mui/material/styles/createTypography'
import type {} from '@mui/x-data-grid-premium/themeAugmentation'
// Font loading is handled via CSS @import or <link> tags rather than next/font.
// See index.html (after Vite migration) or _app.tsx <Head> for the font link tags.
import {
  UIColors,
  amber,
  blue,
  brightBlue,
  gray,
  green,
  orange,
  purple,
  red,
  uiColors,
  white,
  yellow,
} from '~/common/styles/colors'
import { ApplicationOrientation } from '../generated/apollo-operations'
import { DossCardShadow, DossDepth, cardShadow, depth } from './depths'
import { typography } from './typography'

export const dossFont = "'Inter', Helvetica, Arial, sans-serif"
export const dossMonoFont = "'Fragment Mono', 'Inter', Arial, sans-serif"

declare module '@mui/material/styles/createPalette' {
  interface CommonColors {
    doss: {
      blue: typeof blue
      purple: typeof purple
      gray: typeof gray
      green: typeof green
      red: typeof red
      orange: typeof orange
      amber: typeof amber
      yellow: typeof yellow
      white: typeof white
      brightBlue: typeof brightBlue
      dataBorder: {
        normal: string
        hover: string
        dark: string
        light: string
        active: string
        disabled: string
      }
      action: {
        normal: string
        hover: string
        active: string
        disabled: string
      }
    }
  }

  interface TypeBackground {
    white: string
    offWhite: string
    disabled: string
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    body1: TypographyStyleOptions
    body2: TypographyStyleOptions
    body3: TypographyStyleOptions
    heading1: TypographyStyleOptions
    heading2: TypographyStyleOptions
    heading3: TypographyStyleOptions
    label: TypographyStyleOptions
    helperText: TypographyStyleOptions
  }

  // Allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    body1?: TypographyStyleOptions
    body2?: TypographyStyleOptions
    body3?: TypographyStyleOptions
    heading1?: TypographyStyleOptions
    heading2?: TypographyStyleOptions
    heading3?: TypographyStyleOptions
    label?: TypographyStyleOptions
    helperText?: TypographyStyleOptions
  }

  interface TypeText {
    white: string
    danger: string
    warning: string
  }

  interface Palette {
    danger: Palette['error']
    ui: UIColors
  }

  interface PaletteOptions {
    danger?: PaletteOptions['error']
    ui?: UIColors
  }

  interface Theme {
    depth: DossDepth
    cardShadow: DossCardShadow
  }

  interface ThemeOptions {
    depth?: DossDepth
    cardShadow?: DossCardShadow
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    body1: true
    body2: true
    body3: true
    heading1: true
    heading2: true
    heading3: true
    label: true
    helperText: true
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsSizeOverrides {
    xsmall: true
  }
}

// Common props for popper, menu, popover, paper, dialog elevations
const commonElevationProps = {
  defaultProps: {
    borderRadius: '4px',
    elevation: 1,
    PaperProps: {
      elevation: 1,
    },
  },
}

export function createDossTheme(orientation: ApplicationOrientation) {
  // Create a theme instance.
  return createTheme({
    direction: orientation === ApplicationOrientation.RIGHT_TO_LEFT ? 'rtl' : 'ltr',
    palette: {
      primary: {
        main: uiColors.content.primary,
      },
      secondary: {
        main: gray[1],
      },
      success: {
        main: green[9],
      },
      info: {
        main: blue[9],
      },
      warning: {
        main: uiColors.content.warning,
      },
      error: {
        main: uiColors.content.danger,
      },
      danger: {
        main: uiColors.content.danger,
      },
      divider: gray[4],
      text: uiColors.content,
      ui: uiColors,
      common: {
        doss: {
          blue,
          purple,
          gray,
          brightBlue,
          green,
          red,
          orange,
          amber,
          yellow,
          white,
          dataBorder: {
            light: gray[3],
            normal: gray[4],
            dark: gray[5],
            hover: gray[4],
            active: gray[4],
            disabled: '#F3F5F8', // value uses opacity
          },
          action: {
            normal: gray[10],
            hover: gray[9],
            active: '#000000',
            disabled: '#8B8F93', // value uses opacity
          },
        },
      },
      background: uiColors.background,
    },
    typography: {
      fontSize: 12, // Size of body-1
      fontFamily: dossFont,
      body1: typography.body1,
      body2: typography.body2,
      body3: typography.body3,
      heading1: typography.heading1,
      heading2: typography.heading2,
      heading3: typography.heading3,
      label: typography.label,
      helperText: typography.helperText,
    },
    depth,
    cardShadow,
    components: {
      MuiButton: {
        variants: [
          {
            props: { size: 'xsmall' },
            style: {
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              paddingBlock: '8px !important',
              paddingInline: '12px !important',
            },
          },
        ],
        styleOverrides: {
          root: {
            textTransform: 'none',
          },
        },
      },
      MuiToolbar: {
        styleOverrides: {
          regular: {
            minHeight: '56px !important',
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          withBorderColor: {
            borderColor: gray[3],
          },
          columnHeaders: {
            backgroundColor: gray[1],
            minHeight: '36px !important',
            maxHeight: '36px !important',
          },
          row: {
            // Set backgroundColor to prevent flickering on hover state
            backgroundColor: `white !important`,
            '&:hover': {
              backgroundColor: `rgba(0, 0, 0, 0.01) !important`,
            },
          },
          cell: {
            fontSize: '12px',
          },
        },
      },
      MuiPagination: {
        styleOverrides: {
          root: {
            '& .MuiPaginationItem-root': {
              fontSize: '12px',
            },
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            backgroundColor: gray[2],
            borderRadius: '100px',
          },
        },
      },
      MuiCheckbox: {
        defaultProps: {
          disableRipple: true,
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            color: gray[10],
            '&.Mui-disabled': {
              color: gray[6],
            },
            borderRadius: '4px',
            '&:hover,&:active': {
              borderRadius: '4px',
            },
          },
        },
      },
      MuiPaper: { defaultProps: commonElevationProps.defaultProps.PaperProps },
      MuiDialog: commonElevationProps,
      MuiPopover: commonElevationProps,
      MuiMenu: commonElevationProps,
      MuiSwitch: {
        styleOverrides: {
          root: {
            width: 36,
            height: 20,
            padding: 0,
            '& .MuiSwitch-switchBase': {
              padding: 0,
              margin: '2px',
              transitionDuration: '100ms',
              transition: 'transform 0.3s cubic-bezier (0, 0, 0.2, 1) 0ms',
              '&.Mui-checked': {
                transform: 'translateX(16px)',
                color: white,
                '& + .MuiSwitch-track': {
                  backgroundColor: gray[10],
                  opacity: 1,
                  border: 0,
                },
                '&.Mui-disabled + .MuiSwitch-track': {
                  opacity: 0.5,
                },
              },
              '&.Mui-focusVisible .MuiSwitch-thumb': {
                color: gray[10],
                border: `6px solid ${white}`,
              },
              '&.Mui-disabled + .MuiSwitch-track': {
                content: '"On"',
                opacity: 0.7,
              },
            },
            '& .MuiSwitch-thumb': {
              boxSizing: 'border-box',
              width: 16,
              height: 16,
            },
            '& .MuiSwitch-track': {
              borderRadius: 10,
              backgroundColor: gray[1],
              opacity: 1,
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            fontSize: '12px',
          },
        },
      },
    },
  })
}
